import React from 'react';
import AdminPublicLayout from "@/layouts/AdminPublicLayout";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Mail, Send, Loader2, Globe, Inbox, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Helmet } from "react-helmet";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

const Contact = () => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const supportOptions = [
        {
            icon: <Inbox className="w-6 h-6 text-primary" />,
            title: t('contactUs.needSupport.title'),
            description: t('contactUs.needSupport.description'),
        },
        {
            icon: <Mail className="w-6 h-6 text-primary" />,
            title: t('contactUs.feedback.title'),
            description: t('contactUs.feedback.description'),
        },
        {
            icon: <MessageSquare className="w-6 h-6 text-primary" />,
            title: t('contactUs.reportIssue.title'),
            description: t('contactUs.reportIssue.description'),
        },
        {
            icon: <Globe className="w-6 h-6 text-primary" />,
            title: t('contactUs.community.title'),
            description: t('contactUs.community.description'),
        },
    ];

    // Define the form schema with zod
    const formSchema = z.object({
        name: z.string().min(2, {
            message: t('contactUs.nameMin'),
        }),
        email: z.string().email({
            message: t('contactUs.emailInvalid'),
        }),
        message: z.string().min(10, {
            message: t('contactUs.messageMin'),
        }),
    });

    // Initialize form
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            email: "",
            message: "",
        },
    });

    // Form submission handler
    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_PUBLIC_CONTACTUS_ENDPOINT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(values),
            });

            if (!response.ok) {
                throw new Error("Failed to send contact us");
            }
            toast({
                title: t("contactUs.requestSent"),
                description: t("contactUs.weWillGetBack"),
                className: "bg-green-500 text-white border-green-600",
                duration: 5000,
            });
            form.reset();
        } catch (error) {
            console.error('Error submitting contact form:', error);
            toast({
                title: t("contactUs.toastErrorTitle"),
                description: t("contactUs.toastErrorDescription"),
                variant: "destructive",
                duration: 5000,
            });
        }
    };

    return (
        <AdminPublicLayout>
            <Helmet>
                <title>Contact Our Booking Software Experts | FitCalendar</title>
                <link rel="canonical" href="https://fitcalendar.praxipro.io/contact/" />
                <meta name="title" content="Contact Our Booking Software Experts | FitCalendar" />
                <meta name="description" content="Get in touch with the FitCalendar team for support, questions, or partnership inquiries. We’re here to help yoga, Pilates, and fitness studios with booking software." />
                <meta name="author" content="PraxiPro" />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://fitcalendar.praxipro.io/contact/" />
                <meta property="og:title" content="Contact Our Booking Software Experts | FitCalendar" />
                <meta property="og:description" content="Reach out to FitCalendar for assistance, support, or partnership opportunities. Supporting yoga, Pilates, and fitness studios worldwide." />
                <meta property="og:image" content="/og-image.png" />
                <meta property="og:site_name" content="FitCalendar" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://fitcalendar.praxipro.io/contact/" />
                <meta name="twitter:title" content="Contact Our Booking Software Experts | FitCalendar" />
                <meta name="twitter:description" content="Contact the FitCalendar team for questions, support, or partnerships. We help yoga, Pilates, and fitness studios simplify booking." />
                <meta name="twitter:image" content="/og-image.png" />
                <meta name="twitter:creator" content="@PraxiPro" />

                <meta itemProp="name" content="Contact Our Booking Software Experts | FitCalendar" />
                <meta itemProp="description" content="Contact FitCalendar for support, questions, or partnership inquiries related to yoga, Pilates, and fitness booking software." />
                <meta itemProp="image" content="/og-image.png" />

                <meta name="robots" content="index, follow" />
            </Helmet>
            <div className="flex flex-col min-h-screen">
                <main className="flex-1">
                    {/* Hero section */}
                    <section className="bg-primary/5 py-12 md:py-24">
                        <div className="container mx-auto px-4 text-center">
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">
                                {t('contactUs.title')}
                            </h1>
                            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                                {t('contactUs.subHeading')}
                            </p>
                        </div>
                    </section>

                    {/* Contact form and info */}
                    <section className="py-12 md:py-24">
                        <div className="container mx-auto px-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                {/* Contact Information */}
                                <div>
                                    <div className="flex flex-col gap-4 p-4">
                                        {supportOptions.map((item, index) => (
                                            <div
                                                key={index}
                                                className="flex items-start gap-4 p-4 bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition"
                                            >
                                                <div className="mt-1">{item.icon}</div>
                                                <div>
                                                    <h5 className="text-base font-semibold">{item.title}</h5>
                                                    <p className="text-sm text-gray-600">{item.description}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Contact Form */}
                                <div>
                                    <h2 className="text-2xl font-semibold mb-6">
                                        {t('contactUs.fillOutContactForm')}
                                    </h2>

                                    <Form {...form}>
                                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                            <FormField
                                                control={form.control}
                                                name="name"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t('contactUs.name')}</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder={t('contactUs.enterName')} {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="email"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t('contactUs.email')}</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder={t('contactUs.enterEmail')} {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="message"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t('contactUs.message')}</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder={t('contactUs.enterMessage')}
                                                                className="min-h-[120px]"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                                                {form.formState.isSubmitting ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        {t("contactUs.sendingMessage")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="mr-2 h-4 w-4" />
                                                        {t("contactUs.sendMessage")}
                                                    </>
                                                )}
                                            </Button>
                                        </form>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </AdminPublicLayout>
    );
};

export default Contact;